import { Marquee } from '@qse/ui'
import { useCallback, useEffect } from 'react'

const MarqueeSelection = ({
  containerRef,
  isSelecting,
  setIsSelecting,
  setStart,
  setEnd,
  start,
  end,
  finalize,
  shouldStartOnTarget,
  children,
  trashMode = false,
  parentType,
}: {
  containerRef: React.MutableRefObject<HTMLDivElement | null>
  isSelecting: boolean
  setIsSelecting: (v: boolean) => void
  setStart: (pt: { x: number; y: number }) => void
  setEnd: (pt: { x: number; y: number }) => void
  start: { x: number; y: number }
  end: { x: number; y: number }
  finalize: (trashMode?: boolean) => void
  shouldStartOnTarget?: (ev: MouseEvent | React.MouseEvent) => boolean
  children: React.ReactNode
  trashMode?: boolean
  parentType?: 'CHAT_ROOT' | 'CHAT_CONVERSATION'
}) => {
  const getPoint = useCallback(
    (e: MouseEvent | React.MouseEvent) => {
      const host = containerRef.current!
      const rect = host.getBoundingClientRect()
      const x = (e as MouseEvent).clientX - rect.left + host.scrollLeft
      const y = (e as MouseEvent).clientY - rect.top + host.scrollTop

      return { x, y }
    },
    [containerRef],
  )

  useEffect(() => {
    if (!isSelecting) return
    const onMove = (e: MouseEvent) => setEnd(getPoint(e))
    const onUp = () => {
      finalize(trashMode)
      setIsSelecting(false)
    }

    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)

    return () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
  }, [isSelecting, setEnd, finalize, setIsSelecting, trashMode, getPoint])

  const onMouseDown = (e: React.MouseEvent) => {
    // Disable marquee selection in CHAT_ROOT folders (unless in trash mode)
    if (parentType === 'CHAT_ROOT' && !trashMode) {
      return
    }

    // Only handle left-clicks
    if (e.button !== 0) return
    if (!containerRef.current) return
    if (shouldStartOnTarget && !shouldStartOnTarget(e)) return
    setIsSelecting(true)
    const pt = getPoint(e)

    setStart(pt)
    setEnd(pt)
  }

  return (
    <div
      ref={containerRef}
      className="relative pb-5"
      role="grid"
      style={{
        minHeight: 'calc(100dvh - 300px)',
      }}
      onMouseDown={onMouseDown}
    >
      {children}
      {isSelecting && <Marquee end={end} start={start} />}
    </div>
  )
}

export default MarqueeSelection
